// components/EmailPreferencesSettings.tsx
import React, { useState, useEffect } from 'react';
import { AuthService } from '@matchpoint/shared';

export const EmailPreferencesSettings: React.FC = () => {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const authService = AuthService.getInstance();

  useEffect(() => {
    loadEmailPreferences();
  }, []);

  const loadEmailPreferences = async () => {
    try {
      const user = await authService.getCurrentUser();
      if (user?.preferences?.emailNotifications !== undefined) {
        setEmailNotifications(user.preferences.emailNotifications);
      }
    } catch (error) {
      console.error('Failed to load email preferences:', error);
    }
  };

  const handleToggleEmailNotifications = async () => {
    setLoading(true);
    setMessage('');

    try {
      // You would need to add this method to your AuthService
      // For now, we'll simulate the update
      const newValue = !emailNotifications;
      
      // TODO: Implement updateEmailPreferences in AuthService
      // await authService.updateEmailPreferences(newValue);
      
      setEmailNotifications(newValue);
      setMessage('Email preferences updated successfully');
      setMessageType('success');
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
      
    } catch (error: any) {
      setMessage('Failed to update preferences: ' + error.message);
      setMessageType('error');
      
      // Clear message after 5 seconds
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-4 text-gray-900">Email Notifications</h3>
      
      {message && (
        <div className={`mb-4 p-3 rounded-md ${
          messageType === 'success'
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {messageType === 'success' ? (
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm">{message}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <label className="font-medium text-gray-700">
              Email Notifications
            </label>
            <p className="text-sm text-gray-500">
              Receive emails for challenges, matches, and account updates
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleEmailNotifications}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              emailNotifications ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span className="sr-only">Enable email notifications</span>
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                emailNotifications ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="text-xs text-gray-500 mt-4 p-3 bg-gray-50 rounded-md">
          <p><strong>You'll receive emails for:</strong></p>
          <ul className="mt-2 space-y-1">
            <li>🎾 New tennis match challenges</li>
            <li>✅ Challenge acceptances and confirmations</li>
            <li>🔒 Account security notifications</li>
            <li>🔑 Password reset requests</li>
          </ul>
          <p className="mt-2 text-xs">
            <strong>Note:</strong> Critical security emails (like password resets) cannot be disabled.
          </p>
        </div>
      </div>
    </div>
  );
};